import _ from 'lodash';
import { ValidatePlanSection, ValidatePlanTable, ValidatePlanRow, ValidatePlanCellValue } from 'types/tpo/validatePlan';

type ValidationParserConfig = {
    tableId: string;
    tableTitle: string;
    nameField: string;
    nameLabel: string;
    additionalFixedFields?: { field: string; label: string }[];
};

const VALIDATION_CONFIG: Record<string, ValidationParserConfig> = {
    promo_validations: {
        tableId: 'promotion_level',
        tableTitle: 'Promotions',
        nameField: 'promotion_name',
        nameLabel: 'Promotion',
        additionalFixedFields: [{ field: 'promotion_type', label: 'Promotion Type' }],
    },
    tactic_validations: {
        tableId: 'tactic_level',
        tableTitle: 'Tactics',
        nameField: 'tactic',
        nameLabel: 'Tactic',
    },
    product_validations: {
        tableId: 'material_level',
        tableTitle: 'Material Groups',
        nameField: 'material_group',
        nameLabel: 'Material Group',
    },
};
const CONSTRAINT_NAME_LABELS: Record<string, string> = {
    MAX_EVENTS_PER_YEAR_CHECK: 'Max Events per year',
    TOTAL_PROMO_WEEKS_CHECK: 'Trade Promo Weeks',
    PROMOTION_LENGTH_CHECK: 'Promotion length',
    SRP_CHECK: 'Promo SRP',
    GAP_BETWEEN_PROMOTION_CHECK: 'Gap Between Promotions',
    FEATURE_TACTIC_CHECK: 'Enable Feature',
    DISPLAY_TACTIC_CHECK: 'Enable Display',
    TACTIC_QUALITY_CHECK: 'Tactic Quality',
    ENABLED_TACTIC_CHECK: 'Enabled Tactic',
    RETAILER_MARGIN_PERC_CHECK: 'Retailer Margin Perc',
    RETAILER_MARGIN_ABS_CHECK: 'Retailer Margin Abs',
};

type ExpectedValue = {
    type: 'value' | 'range' | 'enum';
    min: number | null;
    max: number | null;
    recommended_value: number | null;
    allowed_values: (string | number)[] | null;
    disallowed_values: (string | number)[] | null;
};

type ConstraintValidation = {
    is_valid: boolean;
    constraint_name: string;
    response_level?: string;
    expected_value: ExpectedValue;
    violated_promotions: any[] | null;
    observed_value: number | string | number[] | string[];
};

type ValidationItem = {
    constraint_validations: ConstraintValidation[];
    [key: string]: any;
};

type PromoOverride = {
    brand: string;
    override_flag: boolean;
};

type KpiSummary = {
    id: string;
    title: string;
    metadata?: Record<string, any>;
    tables: ValidatePlanTable[];
};

type ValidatePlanApiResponse = {
    plan_id: number;
    is_valid: boolean;
    promo_override?: PromoOverride[];
    kpi_summary?: KpiSummary[];
    [key: string]: any;
};

// Creates a structured cell value for constraint violations

const formatObservedValue = (
    observedValue: number | string | number[] | string[],
    constraintName: string,
): number | string | number[] | string[] => {
    // Special formatting for RETAILER_MARGIN_PERC_CHECK: round to 2 decimals and add %
    if (constraintName === 'RETAILER_MARGIN_PERC_CHECK') {
        const roundedObservedValue = roundToDecimalPlaces(observedValue, 2);

        if (typeof roundedObservedValue === 'number' && Number.isFinite(roundedObservedValue)) {
            return `${roundedObservedValue}%`;
        }

        // Handle cases where rounding returned a numeric string
        if (typeof roundedObservedValue === 'string') {
            const n = Number(roundedObservedValue);
            if (Number.isFinite(n)) return `${roundedObservedValue}%`;
        }
        return `${roundedObservedValue}%`;
    }
    if (constraintName === 'RETAILER_MARGIN_ABS_CHECK') {
        if (Array.isArray(observedValue)) return observedValue;

        const roundedObservedValue = roundToDecimalPlaces(observedValue, 2);
        if (typeof roundedObservedValue === 'number' && Number.isFinite(roundedObservedValue)) {
            const isNegative = roundedObservedValue < 0;
            const formattedValue = Math.abs(roundedObservedValue).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return isNegative ? `-$${formattedValue}` : `$${formattedValue}`;
        }
        return roundedObservedValue;
    }
    return observedValue;
};

const createConstraintCellValue = (
    observedValue: number | string | number[] | string[],
    expectedValue: ExpectedValue,
    constraintName: string,
): ValidatePlanCellValue => {
    return {
        observedValue: formatObservedValue(observedValue, constraintName),
        expectedValue: {
            type: expectedValue.type,
            min: expectedValue.min,
            max: expectedValue.max,
            recommendedValue: expectedValue.recommended_value,
            allowedValues: expectedValue.allowed_values,
            disallowedValues: expectedValue.disallowed_values,
        },
        hasExpectedValue: true,
    };
};

/**
 * Gets the column label for a constraint name
 * Falls back to formatting the constraint name if not in mapping
 */
const getConstraintLabel = (constraintName: string): string => {
    if (CONSTRAINT_NAME_LABELS[constraintName]) {
        return CONSTRAINT_NAME_LABELS[constraintName];
    }
    // Fallback: convert SNAKE_CASE to Title Case and remove CHECK suffix
    return constraintName
        .replace(/_CHECK$/, '')
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Extracts unique constraint names from validations array
 */
const getUniqueConstraintNames = (validations: ValidationItem[]): string[] => {
    const constraintNames = new Set<string>();
    validations.forEach((validation) => {
        validation.constraint_validations?.forEach((constraint) => {
            constraintNames.add(constraint.constraint_name);
        });
    });
    return Array.from(constraintNames);
};

const parseValidationsTable = (
    validations: ValidationItem[],
    config: ValidationParserConfig,
): ValidatePlanTable | null => {
    if (!validations || validations.length === 0) {
        return null;
    }

    const constraintNames = getUniqueConstraintNames(validations);

    // Build columns: Name column + dynamic constraint columns
    const columns = [
        { key: config.nameField, label: config.nameLabel },
        // Add additional fixed columns
        ...(config.additionalFixedFields || []).map((f) => ({
            key: f.field,
            label: f.label,
        })),
        ...constraintNames.map((name) => ({
            key: name,
            label: getConstraintLabel(name),
        })),
    ];

    // Build rows
    const rows: ValidatePlanRow[] = validations.map((validation) => {
        const row: ValidatePlanRow = {
            [config.nameField]: validation[config.nameField],
        };
        // Add additional fixed fields
        config.additionalFixedFields?.forEach((f) => {
            row[f.field] = validation[f.field] || '-';
        });
        // Add constraint values as structured data
        validation.constraint_validations?.forEach((constraint) => {
            row[constraint.constraint_name] = createConstraintCellValue(
                constraint.observed_value,
                constraint.expected_value,
                constraint.constraint_name,
            );
        });

        // Fill in missing constraints with dash
        constraintNames.forEach((name) => {
            if (!(name in row)) {
                row[name] = '-';
            }
        });

        return row;
    });

    return {
        id: config.tableId,
        title: config.tableTitle,
        columns,
        rows,
    };
};

/**
 * Parses promo_override into a section
 */
const parsePromoOverrideSection = (promoOverride: PromoOverride[]): ValidatePlanSection | null => {
    if (!promoOverride || promoOverride.length === 0) {
        return null;
    }

    const table: ValidatePlanTable = {
        id: 'brand_override_table',
        columns: [
            { key: 'brand', label: 'Brand' },
            { key: 'override', label: 'Override' },
        ],
        rows: promoOverride.map((item) => ({
            brand: item.brand,
            override: item.override_flag ? 'Yes' : 'No',
        })),
    };

    return {
        id: 'impact_override',
        title: 'Impact of promo incremental override',
        tables: [table],
    };
};

const roundToDecimalPlaces = (value: any, decimals: number = 2): any => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return _.round(value, decimals);
    }
    if (typeof value === 'string') {
        const numValue = Number(value);
        if (Number.isFinite(numValue)) {
            return _.round(numValue, decimals);
        }
    }
    return value;
};

/**
 * Rounds all numeric values in a table row to specified decimal places
 */
const roundTableRowValues = (row: ValidatePlanRow, decimals: number = 2): ValidatePlanRow => {
    const roundedRow: ValidatePlanRow = {};
    Object.entries(row).forEach(([key, value]) => {
        roundedRow[key] = roundToDecimalPlaces(value, decimals);
    });
    return roundedRow;
};

/**
 * Rounds all numeric values in a table's rows
 */
const roundTableValues = (table: ValidatePlanTable, decimals: number = 2): ValidatePlanTable => {
    return {
        ...table,
        rows: table.rows.map((row) => roundTableRowValues(row, decimals)),
    };
};

/**
 * Parses KPI summary section - only metadata, no tables
 */
const parseKpiSummarySection = (kpiSummary: KpiSummary[]): ValidatePlanSection | null => {
    if (!kpiSummary || kpiSummary.length === 0) {
        return null;
    }

    const kpi = kpiSummary[0];

    // Round numeric values in KPI tables to 2 decimal places
    //removing table for now as per requirement for now
    // const roundedTables = (kpi.tables || []).map((table) => roundTableValues(table, 2));

    return {
        id: kpi.id || 'kpi_summary',
        title: kpi.title || 'Concurrent Promotions',
        metadata: kpi.metadata,
        tables: [], // Only show metadata, not the tables
    };
};

/**
 * Main parser function to transform API response into ValidatePlanSection[]
 */
export const parseValidatePlanResponse = (data: ValidatePlanApiResponse): ValidatePlanSection[] => {
    const sections: ValidatePlanSection[] = [];

    // 1. Parse promo_override section (special structure)
    const overrideSection = parsePromoOverrideSection(data.promo_override || []);
    if (overrideSection) {
        sections.push(overrideSection);
    }

    // 2. Parse all validation sections dynamically using config
    const validationTables: ValidatePlanTable[] = [];

    // Loop through all configured validation types
    Object.entries(VALIDATION_CONFIG).forEach(([validationKey, config]) => {
        const validations = data[validationKey];
        if (validations && Array.isArray(validations) && validations.length > 0) {
            const table = parseValidationsTable(validations, config);
            if (table) {
                validationTables.push(table);
            }
        }
    });

    // If there are any validation tables, create the constraints section
    if (validationTables.length > 0) {
        const constraintsSection: ValidatePlanSection = {
            id: 'constraints_violated',
            title: 'Constraints violated in the plan',
            severity: 'error',
            tables: validationTables,
        };
        sections.push(constraintsSection);
    }

    // 3. Parse KPI summary section
    const kpiSection = parseKpiSummarySection(data.kpi_summary || []);
    if (kpiSection) {
        sections.push(kpiSection);
    }

    return sections;
};

export default parseValidatePlanResponse;
